import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Grid, Key } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  columnsUsed: number[];
  averageColumns: number;
  totalTranspositions: number;
  uniqueKeywords: Set<string>;
  averageKeywordLength: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  keyword: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  columnCount: number;
}

const ColumnarTranspositionCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [keyword, setKeyword] = useState<string>('CIPHER');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'grid' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [preserveSpaces, setPreserveSpaces] = useState<boolean>(false);
  const [preservePunctuation, setPreservePunctuation] = useState<boolean>(false);
  const [showGrid, setShowGrid] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(true);
  const [groupSize, setGroupSize] = useState<number>(5);
  const [fillChar, setFillChar] = useState<string>('X');
  const [irregularColumns, setIrregularColumns] = useState<boolean>(false);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    columnsUsed: [],
    averageColumns: 0,
    totalTranspositions: 0,
    uniqueKeywords: new Set<string>(),
    averageKeywordLength: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [columnGrid, setColumnGrid] = useState<string[][]>([]);
  const [columnOrder, setColumnOrder] = useState<number[]>([]);

  // Clean text based on preservation settings
  const cleanText = useCallback((text: string): string => {
    let cleaned = text;
    
    if (!preserveSpaces) {
      cleaned = cleaned.replace(/\s/g, '');
    }
    
    if (!preservePunctuation) {
      cleaned = cleaned.replace(/[^\w\s]/g, '');
    }
    
    if (!preserveCase) {
      cleaned = cleaned.toUpperCase();
    }
    
    return cleaned;
  }, [preserveSpaces, preservePunctuation, preserveCase]);

  // Generate column order from keyword
  const generateColumnOrder = useCallback((key: string): number[] => {
    if (!key) return [];
    
    const cleanKey = key.replace(/[^A-Za-z]/g, '').toUpperCase();
    const keyChars = cleanKey.split('');
    const uniqueChars = Array.from(new Set(keyChars));
    const sortedChars = Array.from(uniqueChars).sort();
    
    // Map each character to its alphabetical position
    return keyChars.map(char => sortedChars.indexOf(char));
  }, []);

  // Generate grid visualization
  const generateGrid = useCallback((text: string, key: string): { grid: string[][], order: number[] } => {
    if (!text || !key) return { grid: [], order: [] };
    
    const cleanedText = cleanText(text);
    const cleanKey = key.replace(/[^A-Za-z]/g, '').toUpperCase();
    const columnCount = cleanKey.length;
    const order = generateColumnOrder(key);
    
    if (columnCount === 0) return { grid: [], order: [] };
    
    // Calculate rows needed
    const rowCount = Math.ceil(cleanedText.length / columnCount);
    const grid: string[][] = Array(rowCount).fill(null).map(() => Array(columnCount).fill(''));
    
    // Fill grid row by row
    let textIndex = 0;
    for (let row = 0; row < rowCount; row++) {
      for (let col = 0; col < columnCount; col++) {
        if (textIndex < cleanedText.length) {
          grid[row][col] = cleanedText[textIndex];
          textIndex++;
        } else if (!irregularColumns) {
          // Fill incomplete rows with fill character
          grid[row][col] = fillChar;
        }
      }
    }
    
    return { grid, order };
  }, [cleanText, generateColumnOrder, fillChar, irregularColumns]);

  // Columnar Transposition encryption
  const columnarEncrypt = useCallback((text: string, key: string): string => {
    if (!text || !key) return '';
    
    const { grid, order } = generateGrid(text, key);
    if (grid.length === 0) return '';
    
    let result = '';
    const columnCount = order.length;
    
    // Read columns in sorted order
    for (let orderIndex = 0; orderIndex < columnCount; orderIndex++) {
      const colIndex = order.indexOf(orderIndex);
      if (colIndex !== -1) {
        for (let row = 0; row < grid.length; row++) {
          if (grid[row][colIndex] && (irregularColumns || grid[row][colIndex] !== fillChar || row < grid.length - 1 || colIndex < columnCount - 1)) {
            result += grid[row][colIndex];
          }
        }
      }
    }
    
    // Apply grouping if enabled
    if (groupOutput && result) {
      const groups = [];
      for (let i = 0; i < result.length; i += groupSize) {
        groups.push(result.slice(i, i + groupSize));
      }
      result = groups.join(' ');
    }
    
    return result;
  }, [generateGrid, fillChar, irregularColumns, groupOutput, groupSize]);

  // Columnar Transposition decryption
  const columnarDecrypt = useCallback((text: string, key: string): string => {
    if (!text || !key) return '';
    
    // Remove spaces if text was grouped
    const cleanedText = text.replace(/\s/g, '');
    const cleanKey = key.replace(/[^A-Za-z]/g, '').toUpperCase();
    const columnCount = cleanKey.length;
    const order = generateColumnOrder(key);
    
    if (columnCount === 0 || cleanedText.length === 0) return '';
    
    // Calculate grid dimensions
    const totalChars = cleanedText.length;
    const rowCount = Math.ceil(totalChars / columnCount);
    const fullRows = Math.floor(totalChars / columnCount);
    const remainingChars = totalChars % columnCount;
    
    // Create empty grid
    const grid: string[][] = Array(rowCount).fill(null).map(() => Array(columnCount).fill(''));
    
    // Calculate how many characters each column should have
    const columnLengths = Array(columnCount).fill(fullRows);
    if (remainingChars > 0 && !irregularColumns) {
      // In regular mode, distribute remaining characters
      for (let i = 0; i < remainingChars; i++) {
        columnLengths[i]++;
      }
    } else if (remainingChars > 0) {
      // In irregular mode, last columns get extra characters
      for (let i = 0; i < remainingChars; i++) {
        columnLengths[columnCount - remainingChars + i]++;
      }
    }
    
    // Fill grid column by column in sorted order
    let textIndex = 0;
    for (let orderIndex = 0; orderIndex < columnCount; orderIndex++) {
      const colIndex = order.indexOf(orderIndex);
      if (colIndex !== -1) {
        const colLength = columnLengths[colIndex];
        for (let row = 0; row < colLength; row++) {
          if (textIndex < cleanedText.length) {
            grid[row][colIndex] = cleanedText[textIndex];
            textIndex++;
          }
        }
      }
    }
    
    // Read grid row by row
    let result = '';
    for (let row = 0; row < rowCount; row++) {
      for (let col = 0; col < columnCount; col++) {
        if (grid[row][col] && grid[row][col] !== fillChar) {
          result += grid[row][col];
        }
      }
    }
    
    return result;
  }, [generateColumnOrder, fillChar, irregularColumns]);

  // Process text - main cipher logic
  const processText = useCallback(() => {
    if (!keyword.trim()) {
      setOutputText('');
      return '';
    }
    
    const result = operation === 'encrypt' 
      ? columnarEncrypt(inputText, keyword)
      : columnarDecrypt(inputText, keyword);
    
    setOutputText(result);
    return result;
  }, [inputText, keyword, operation, columnarEncrypt, columnarDecrypt]);

  // Update analytics
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !keyword.trim() || !result) return;
    
    const cleanInput = cleanText(inputText);
    const columnCount = keyword.replace(/[^A-Za-z]/g, '').length;
    
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      keyword: keyword,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      columnCount: columnCount
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);
    
    setStats(prevStats => {
      const newColumnsUsed = [...prevStats.columnsUsed, columnCount];
      const newUniqueKeywords = new Set(Array.from(prevStats.uniqueKeywords).concat([keyword.toUpperCase()]));
      const totalOps = prevStats.totalOperations + 1;
      const totalChars = prevStats.totalCharactersProcessed + inputText.length;
      
      return {
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: totalOps,
        totalCharactersProcessed: totalChars,
        averageTextLength: Math.round(totalChars / totalOps),
        longestText: Math.max(prevStats.longestText, inputText.length),
        columnsUsed: newColumnsUsed,
        averageColumns: Math.round(newColumnsUsed.reduce((sum, c) => sum + c, 0) / newColumnsUsed.length),
        totalTranspositions: prevStats.totalTranspositions + 1,
        uniqueKeywords: newUniqueKeywords,
        averageKeywordLength: Math.round(Array.from(newUniqueKeywords).reduce((sum, k) => sum + k.length, 0) / newUniqueKeywords.size)
      };
    });
  }, [inputText, keyword, operation, cleanText]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText && keyword) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, keyword, operation]);

  // Update grid when text or keyword changes
  useEffect(() => {
    if (inputText && keyword) {
      const { grid, order } = generateGrid(inputText, keyword);
      setColumnGrid(grid);
      setColumnOrder(order);
    }
  }, [inputText, keyword, generateGrid]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setKeyword('CIPHER');
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/\s/g, ''));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "ATTACK AT DAWN FROM THE EAST GATE",
      "THE ENEMY IS APPROACHING FROM THE NORTH",
      "REINFORCEMENTS NEEDED AT SECTOR SEVEN",
      "MISSION ACCOMPLISHED RETURN TO BASE",
      "COLUMNAR TRANSPOSITION IS VERY SECURE",
      "SECRET AGENT DOUBLE OH SEVEN REPORTING",
      "INTELLIGENCE SUGGESTS INVASION IMMINENT"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateRandomKeyword = () => {
    const words = [
      'CIPHER', 'SECRET', 'CRYPTO', 'MATRIX', 'COLUMN', 
      'TRANSPOSE', 'ENIGMA', 'DECODE', 'SECURE', 'HIDDEN',
      'MILITARY', 'STEALTH', 'CLASSIFIED', 'OPERATION'
    ];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    setKeyword(randomWord);
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Columnar Transposition Cipher - Advanced Column-Based Encryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional Columnar Transposition Cipher tool with grid visualization, keyword-based column ordering, and historical military cipher features. Perfect for cryptography education and classical cipher research." />
        <meta name="keywords" content="columnar transposition cipher, column cipher, transposition cryptography, grid cipher, classical cryptography, military cipher, keyword cipher, matrix encryption, columnar encryption, cipher grid, historical encryption, cryptanalysis, security education" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Columnar Transposition Cipher - Advanced Column-Based Tool" />
        <meta property="og:description" content="Professional Columnar Transposition Cipher with grid visualization, keyword-based ordering, and historical military encryption features." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="Columnar Transposition Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Columnar Transposition Cipher - Historical Military Encryption" />
        <meta name="twitter:description" content="Professional Columnar Transposition Cipher with grid visualization and educational features. Free classical cryptography tool." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@columnarcipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Columnar Transposition Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Historical & Military Keywords */}
        <meta name="subject" content="Military History, Cryptography, Classical Ciphers, World War Communications" />
        <meta name="classification" content="Education, History, Military Science, Computer Science" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="Columnar Transposition Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Columnar Transposition Cipher Tool",
            "alternateName": ["Columnar Cipher", "Column Transposition Tool", "Grid Cipher", "Matrix Cipher"],
            "description": "Advanced Columnar Transposition Cipher tool for column-based encryption with grid visualization, keyword ordering, and historical military cipher features.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": ["SecurityApplication", "EducationalApplication", "HistoricalApplication"],
            "operatingSystem": "Any",
            "browserRequirements": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Real-time Columnar Transposition encryption and decryption",
              "Interactive grid visualization",
              "Keyword-based column ordering",
              "Historical military cipher implementation",
              "Grid generation and display",
              "Advanced cipher options and settings",
              "Analytics and usage statistics",
              "Educational cipher information",
              "Dark and light themes",
              "File upload and download",
              "Mobile responsive design"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "1.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "Columnar Transposition Cipher Tool",
              "description": "Historical cryptography and column-based cipher education tools"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "teaches",
              "educationalFramework": "Military History and Cryptography Education",
              "targetName": "Classical Cryptography and Historical Military Communications"
            },
            "learningResourceType": "Interactive Tool",
            "educationalUse": ["instruction", "research", "historical study"],
            "typicalAgeRange": "14-99",
            "inLanguage": "en",
            "isAccessibleForFree": true,
            "about": [
              {
                "@type": "Thing",
                "name": "Military Cryptography"
              },
              {
                "@type": "Thing",
                "name": "World War Communications"
              },
              {
                "@type": "Thing",
                "name": "Transposition Cipher Systems"
              }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "967",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
        
        {/* Historical Military Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HistoricalDigitalObject",
            "name": "Columnar Transposition Cipher Historical Implementation",
            "description": "Digital recreation of the Columnar Transposition cipher system used in military communications during both World Wars and classical cryptography.",
            "dateCreated": "1900s",
            "creator": {
              "@type": "Organization",  
              "name": "Military Cryptographers"
            },
            "about": {
              "@type": "HistoricalEvent",
              "name": "Military Cryptography Development"
            },
            "culturalContext": "Early 20th Century Military Communications",
            "historicalSignificance": "Widely used transposition cipher in military communications during WWI and WWII"
          })}
        </script>
        
        {/* Educational Military History Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": "Columnar Transposition Cipher Military History Tutorial",
            "description": "Learn about military cryptography through hands-on interaction with the historic Columnar Transposition cipher system used in WWI and WWII communications.",
            "educationalLevel": "intermediate",
            "teaches": [
              "Columnar transposition ciphers",
              "Military cryptography history", 
              "Grid-based encryption methods",
              "World War communication techniques",
              "Classical cipher analysis"
            ],
            "learningResourceType": "Interactive Tool",
            "interactivityType": "active",
            "educationalUse": ["instruction", "research", "historical analysis"],
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": ["student", "teacher", "historian", "researcher"],
              "audienceType": ["military history student", "cryptography enthusiast"]
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Military History"
              },
              {
                "@type": "Thing",
                "name": "World War Communications"
              },
              {
                "@type": "Thing",
                "name": "Transposition Cryptography"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Grid className="text-green-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Columnar Transposition Cipher</h1>
                <p className="text-sm opacity-60">Column-Based Transposition Cipher (1900s)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Grid Viewer */}
              <button
                onClick={() => setActiveModal(activeModal === 'grid' ? null : 'grid')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="View Column Grid"
              >
                <Grid size={20} />
              </button>
              
              {/* Help */}
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              {/* Advanced Options */}
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              {/* Analytics */}
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">🔒 Encrypt</option>
                    <option value="decrypt">🔓 Decrypt</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Keyword</label>
                    <button
                      onClick={generateRandomKeyword}
                      className={`text-xs px-2 py-1 rounded ${
                        isDarkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
                      } text-white transition-colors`}
                      title="Generate Random Keyword"
                    >
                      Random
                    </button>
                  </div>
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())}
                    placeholder="Enter keyword for columns..."
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                  <div className="text-xs opacity-60 mt-1">
                    {keyword ? `${keyword.length} columns` : 'No keyword'}
                  </div>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Key size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {/* Column Order Preview */}
              {keyword && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Column Order for "{keyword}":</h4>
                  <div className="flex items-center space-x-4">
                    <div>
                      <div className="text-xs opacity-60 mb-1">Original:</div>
                      <div className="flex space-x-1">
                        {keyword.split('').map((char, index) => (
                          <div
                            key={index}
                            className={`w-8 h-8 flex items-center justify-center text-sm font-mono font-bold border ${
                              isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          >
                            {char}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-xs opacity-60 mb-1">Reading Order:</div>
                      <div className="flex space-x-1">
                        {columnOrder.map((order, index) => (
                          <div
                            key={index}
                            className={`w-8 h-8 flex items-center justify-center text-sm font-mono font-bold border ${
                              isDarkMode ? 'bg-green-600 border-green-500 text-white' : 'bg-green-100 border-green-300 text-green-800'
                            }`}
                          >
                            {order + 1}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Grid Preview */}
              {showGrid && columnGrid.length > 0 && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Column Grid ({columnGrid.length} rows × {columnGrid[0]?.length || 0} columns):</h4>
                  <div className="overflow-x-auto">
                    <div className="min-w-fit">
                      {/* Header with column letters */}
                      <div className="flex mb-1">
                        <div className="w-8"></div>
                        {keyword.split('').map((char, index) => (
                          <div
                            key={index}
                            className={`w-8 h-6 flex items-center justify-center text-xs font-bold ${
                              isDarkMode ? 'text-green-400' : 'text-green-600'
                            }`}
                          >
                            {char}
                          </div>
                        ))}
                      </div>
                      
                      {/* Header with column order */}
                      <div className="flex mb-2">
                        <div className="w-8"></div>
                        {columnOrder.map((order, index) => (
                          <div
                            key={index}
                            className={`w-8 h-6 flex items-center justify-center text-xs font-bold ${
                              isDarkMode ? 'text-blue-400' : 'text-blue-600'
                            }`}
                          >
                            {order + 1}
                          </div>
                        ))}
                      </div>
                      
                      {/* Grid rows */}
                      {columnGrid.map((row, rowIndex) => (
                        <div key={rowIndex} className="flex">
                          <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {rowIndex + 1}
                          </div>
                          {row.map((cell, colIndex) => (
                            <div
                              key={colIndex}
                              className={`w-8 h-8 flex items-center justify-center text-sm font-mono font-bold border ${
                                cell && cell !== fillChar
                                  ? (isDarkMode ? 'bg-yellow-900 text-yellow-300 border-yellow-700' : 'bg-yellow-100 text-yellow-800 border-yellow-300')
                                  : (isDarkMode ? 'bg-gray-600 border-gray-500 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500')
                              }`}
                            >
                              {cell || '·'}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encrypt or decrypt using Columnar Transposition cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="mt-2 flex items-center space-x-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                    <Upload size={16} className="mr-1" />
                    <span className="text-sm">Upload File</span>
                  </label>
                </div>
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'columnar-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Grid Modal */}
        {activeModal === 'grid' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-6xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Grid className="text-green-500" />
                  <span>Column Grid</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="text-center">
                <div className="mb-4">
                  <div className="text-sm opacity-60 mb-2">
                    Keyword: <strong>{keyword}</strong> | 
                    Text: <strong>{cleanText(inputText).substring(0, 30)}{cleanText(inputText).length > 30 ? '...' : ''}</strong>
                  </div>
                </div>
                
                {columnGrid.length > 0 && (
                  <div className="overflow-auto mb-4 max-h-96">
                    <div className="inline-block min-w-fit">
                      {/* Column headers */}
                      <div className="flex mb-2">
                        <div className="w-10 h-8 flex items-center justify-center font-bold text-sm">
                          Row
                        </div>
                        {keyword.split('').map((char, index) => (
                          <div key={index} className="text-center mx-1">
                            <div className={`w-10 h-6 flex items-center justify-center text-sm font-bold ${
                              isDarkMode ? 'text-green-400' : 'text-green-600'
                            }`}>
                              {char}
                            </div>
                            <div className={`w-10 h-6 flex items-center justify-center text-xs font-bold ${
                              isDarkMode ? 'text-blue-400' : 'text-blue-600'
                            }`}>
                              {columnOrder[index] + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Grid content */}
                      {columnGrid.map((row, rowIndex) => (
                        <div key={rowIndex} className="flex mb-1">
                          <div className={`w-10 h-10 flex items-center justify-center text-sm font-bold border ${
                            isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-700'
                          }`}>
                            {rowIndex + 1}
                          </div>
                          {row.map((cell, colIndex) => (
                            <div
                              key={colIndex}
                              className={`w-10 h-10 flex items-center justify-center text-sm font-mono font-bold border mx-1 ${
                                cell && cell !== fillChar
                                  ? (isDarkMode ? 'bg-yellow-900 text-yellow-300 border-yellow-700' : 'bg-yellow-100 text-yellow-800 border-yellow-300')
                                  : (isDarkMode ? 'bg-gray-600 border-gray-500 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500')
                              }`}
                            >
                              {cell || '·'}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="text-xs opacity-60 space-y-1">
                  <div>Text is arranged row by row, then read column by column in alphabetical order</div>
                  <div>Numbers show the reading order of columns based on keyword</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Shield className="text-green-500" />
                  <span>About Columnar Transposition Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Columnar Transposition Cipher?</h3>
                  <p>The Columnar Transposition Cipher is a classical transposition cipher that arranges plaintext in a rectangular grid and then reads the columns in a specific order determined by a keyword. Unlike substitution ciphers, it rearranges the letters rather than replacing them, making it a pure transposition cipher widely used in military communications.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Choose a keyword (e.g., "CIPHER")</li>
                    <li>Write the keyword above columns</li>
                    <li>Number columns based on alphabetical order of keyword letters</li>
                    <li>Write the plaintext in rows under the columns</li>
                    <li>Fill incomplete rows with padding if needed</li>
                    <li>Read columns in numerical order to get ciphertext</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Example with keyword "CIPHER"</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="font-mono text-xs space-y-1">
                      <div><strong>Plaintext:</strong> "ATTACK AT DAWN"</div>
                      <div className="mt-2"><strong>Keyword:</strong> C I P H E R</div>
                      <div><strong>Column Order:</strong> 1 3 5 4 2 6</div>
                      <div className="mt-2"><strong>Grid Layout:</strong></div>
                      <div>C I P H E R</div>
                      <div>1 3 5 4 2 6</div>
                      <div>A T T A C K</div>
                      <div>A T D A W N</div>
                      <div className="mt-2"><strong>Reading by columns:</strong></div>
                      <div>Col 1 (C): AA</div>
                      <div>Col 2 (E): CW</div>
                      <div>Col 3 (I): TT</div>
                      <div>Col 4 (H): AA</div>
                      <div>Col 5 (P): TD</div>
                      <div>Col 6 (R): KN</div>
                      <div><strong>Ciphertext:</strong> AACWTTAATDKN</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Military History</h3>
                  <ul className="space-y-1">
                    <li><strong>World War I:</strong> Used by various military forces for field communications</li>
                    <li><strong>World War II:</strong> Enhanced versions used in diplomatic and military messages</li>
                    <li><strong>Telegraph Era:</strong> Secured long-distance military communications</li>
                    <li><strong>Resistance Movements:</strong> Used by underground networks during WWII</li>
                    <li><strong>Naval Communications:</strong> Protected ship-to-shore communications</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Encryption Process</h3>
                  <ul className="space-y-1">
                    <li><strong>Keyword Setup:</strong> Arrange keyword letters as column headers</li>
                    <li><strong>Column Numbering:</strong> Number columns by alphabetical order</li>
                    <li><strong>Text Placement:</strong> Fill grid row by row with plaintext</li>
                    <li><strong>Padding:</strong> Add filler characters to complete last row</li>
                    <li><strong>Column Reading:</strong> Read columns in numerical order</li>
                    <li><strong>Concatenation:</strong> Join column contents for final cipher</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Decryption Process</h3>
                  <ul className="space-y-1">
                    <li><strong>Grid Calculation:</strong> Determine grid dimensions from text length</li>
                    <li><strong>Column Distribution:</strong> Calculate characters per column</li>
                    <li><strong>Column Filling:</strong> Fill columns in numerical order</li>
                    <li><strong>Grid Reconstruction:</strong> Rebuild the original grid</li>
                    <li><strong>Row Reading:</strong> Read grid row by row</li>
                    <li><strong>Plaintext Recovery:</strong> Remove padding to get original message</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Advantages</h3>
                  <ul className="space-y-1">
                    <li>More secure than simple transposition ciphers</li>
                    <li>Keyword provides variable security level</li>
                    <li>Can be combined with substitution ciphers</li>
                    <li>Relatively fast to encrypt and decrypt manually</li>
                    <li>No character frequency information leaked</li>
                    <li>Scalable security with longer keywords</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Weaknesses</h3>
                  <ul className="space-y-1">
                    <li>Vulnerable to known plaintext attacks</li>
                    <li>Pattern analysis can reveal keyword length</li>
                    <li>Anagram solving techniques can break it</li>
                    <li>Frequency analysis of letter positions possible</li>
                    <li>Limited by keyword choice and length</li>
                    <li>Rectangular grid pattern is predictable</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Keyword Strategy</h3>
                  <ul className="space-y-1">
                    <li><strong>Length:</strong> 4-8 characters optimal for manual use</li>
                    <li><strong>Uniqueness:</strong> No repeated letters for standard version</li>
                    <li><strong>Memorability:</strong> Easy to remember but hard to guess</li>
                    <li><strong>Randomness:</strong> Avoid common words or patterns</li>
                    <li><strong>Security:</strong> Longer keywords provide better security</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Variations</h3>
                  <ul className="space-y-1">
                    <li><strong>Irregular Columnar:</strong> Incomplete final columns</li>
                    <li><strong>Double Columnar:</strong> Apply cipher twice with different keywords</li>
                    <li><strong>Block Columnar:</strong> Multiple small grids instead of one large</li>
                    <li><strong>Keyed Columnar:</strong> Both keyword and numerical key</li>
                    <li><strong>Route Cipher:</strong> Non-standard reading patterns</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Modern Applications</h3>
                  <ul className="space-y-1">
                    <li>Cryptography education and training</li>
                    <li>Historical cipher demonstrations</li>
                    <li>Programming and algorithm exercises</li>
                    <li>Puzzle creation and recreational cryptography</li>
                    <li>Understanding transposition principles</li>
                    <li>Military history and communication studies</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Breaking Techniques</h3>
                  <ul className="space-y-1">
                    <li><strong>Frequency Analysis:</strong> Analyze letter position patterns</li>
                    <li><strong>Known Plaintext:</strong> Use known message segments</li>
                    <li><strong>Anagram Analysis:</strong> Rearrange blocks to find words</li>
                    <li><strong>Keyword Guessing:</strong> Try common keywords systematically</li>
                    <li><strong>Pattern Recognition:</strong> Look for repeated structures</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-orange-600">⚠️ <strong>Historical Security:</strong> The Columnar Transposition cipher provided good security for its era, especially when combined with other techniques. However, it can be broken by modern cryptanalytic methods and computer analysis. It's excellent for education but not suitable for actual security needs.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Case</label>
                  <input
                    type="checkbox"
                    checked={preserveCase}
                    onChange={(e) => setPreserveCase(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Spaces</label>
                  <input
                    type="checkbox"
                    checked={preserveSpaces}
                    onChange={(e) => setPreserveSpaces(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Punctuation</label>
                  <input
                    type="checkbox"
                    checked={preservePunctuation}
                    onChange={(e) => setPreservePunctuation(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Column Grid</label>
                  <input
                    type="checkbox"
                    checked={showGrid}
                    onChange={(e) => setShowGrid(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Irregular Columns</label>
                  <input
                    type="checkbox"
                    checked={irregularColumns}
                    onChange={(e) => setIrregularColumns(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {groupOutput && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Group Size</label>
                    <input
                      type="range"
                      min="3"
                      max="10"
                      value={groupSize}
                      onChange={(e) => setGroupSize(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-xs opacity-60 mt-1">
                      Characters per group: {groupSize}
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-2">Fill Character</label>
                  <input
                    type="text"
                    value={fillChar}
                    onChange={(e) => setFillChar(e.target.value.replace(/[^A-Z]/g, '').slice(0, 1) || 'X')}
                    placeholder="X"
                    maxLength={1}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                  <div className="text-xs opacity-60 mt-1">
                    Character used to fill incomplete rows
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Historical Keywords</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {['CIPHER', 'SECRET', 'MATRIX', 'COLUMN', 'CRYPTO', 'ENCODE'].map(key => (
                      <button
                        key={key}
                        onClick={() => setKeyword(key)}
                        className={`p-2 text-xs rounded border ${
                          keyword === key
                            ? (isDarkMode ? 'bg-green-600 border-green-500 text-white' : 'bg-green-500 border-green-400 text-white')
                            : (isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200')
                        } transition-colors`}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Military Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setPreserveCase(false);
                        setPreserveSpaces(false);
                        setPreservePunctuation(false);
                        setGroupOutput(true);
                        setGroupSize(5);
                        setFillChar('X');
                        setIrregularColumns(false);
                        setKeyword('CIPHER');
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      WWI Military Standard
                    </button>
                    
                    <button
                      onClick={() => {
                        setPreserveCase(true);
                        setPreserveSpaces(true);
                        setPreservePunctuation(true);
                        setGroupOutput(false);
                        setFillChar('Z');
                        setIrregularColumns(true);
                        setKeyword('MATRIX');
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                      } transition-colors`}
                    >
                      Modern Educational
                    </button>
                    
                    <button
                      onClick={() => {
                        setPreserveCase(false);
                        setPreserveSpaces(false);
                        setPreservePunctuation(false);
                        setGroupOutput(true);
                        setGroupSize(4);
                        setFillChar('Q');
                        setIrregularColumns(false);
                        setKeyword('SECURE');
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                      } transition-colors`}
                    >
                      Telegraph Security
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.totalTranspositions}</div>
                  <div className="text-sm">Total Transpositions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Characters Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.uniqueKeywords.size}</div>
                  <div className="text-sm">Unique Keywords</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.averageKeywordLength}</div>
                  <div className="text-sm">Avg Keyword Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-amber-500">{stats.averageColumns}</div>
                  <div className="text-sm">Avg Columns</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.columnCount} cols
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-green-500 text-white">
                            {item.keyword}
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Grid size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Cipher Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-purple-500">Most Used Keyword:</div>
                      <div>
                        {history.length > 0 
                          ? (() => {
                              const counts = history.reduce((acc, item) => {
                                acc[item.keyword] = (acc[item.keyword] || 0) + 1;
                                return acc;
                              }, {} as Record<string, number>);
                              return Object.keys(counts).reduce((a, b) => counts[a] > counts[b] ? a : b);
                            })()
                          : 'N/A'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="text-green-500">Encrypt/Decrypt Ratio:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.totalEncryptions / stats.totalOperations) * 100) : 0}% Encrypt</div>
                    </div>
                    <div>
                      <div className="text-blue-500">Most Active Time:</div>
                      <div>{history.length > 0 ? new Date(Math.max(...history.map(h => h.timestamp.getTime()))).toLocaleTimeString() : 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-orange-500">Efficiency:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.totalTranspositions / stats.totalOperations) * 100) : 0}% Success</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ColumnarTranspositionCipherApp;